import clsx from 'clsx'
import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { Pressable, View } from 'react-native'

import { Iframe } from '@/src/elements/iframe'
import { AudioPlayer } from '@/src/elements/media/audio/AudioPlayer'
import { Icon } from '@/src/elements/media/Icon'
import { Typography } from '@/src/elements/text/Typography'
import { ICON } from '@/src/general/enums/icons'
import { useTranslate } from '@/src/lang/useTranslate'

import { Card, type TCardProps } from '../Card'
import { CardBody } from '../CardBody'
import { formatAudioSource } from './formatAudioSource'

export type TAudioCardProps = {
  title?: string
  source: string
  iframeProps?: Record<string, unknown>
  'data-content-type'?: string
} & TCardProps

export const AudioCard = ({ classNames, title, source, iframeProps, ...otherProps }: TAudioCardProps): ReactNode => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [showContent, setShowContent] = useState(false)
  const [isControlElementFocused, setIsControlElementFocused] = useState(false)
  const { t } = useTranslate()

  const dataContentType = otherProps?.['data-content-type']

  const shouldHideCard = useMemo(
    () => !source.endsWith('.mp3') || !dataContentType?.includes('audio/'),
    [source, dataContentType],
  )

  const handleFinished = useCallback(() => setIsPlaying(false), [])

  const handlePress = useCallback(() => {
    setIsPlaying((prev) => !prev)
    setShowContent(true)
  }, [])

  if (isPlaying && shouldHideCard) {
    const sourceObject = formatAudioSource(source)
    return <Iframe title={title} src={sourceObject.toString()} {...iframeProps} />
  }

  return (
    <Card
      classNames={{
        base: clsx('bg-primary-bg-light', classNames?.base),
        body: clsx('p-3 native:justify-center', classNames?.body),
      }}
      radius="lg"
      {...otherProps}
    >
      <CardBody className="native:max-h-32">
        <Pressable
          dataSet={{ 'focus-visible': isControlElementFocused }}
          onFocus={() => setIsControlElementFocused(true)}
          onBlur={() => setIsControlElementFocused(false)}
          className="flex w-full flex-row items-center justify-between rounded-md
          px-1
          outline-none
          web:data-[focus-visible=true]:outline-4
          web:data-[focus-visible=true]:outline-offset-1 web:data-[focus-visible=true]:outline-layout-focus"
          onPress={handlePress}
          // @ts-expect-error onMouseDown is a React Native Web-only prop
          onMouseDown={(e: MouseEvent) => e.preventDefault()} // This is so focus is not triggered on click
        >
          <View className="flex max-w-[90%] flex-initial flex-wrap">
            <Typography bold size="lg" colors="primary">
              {t('app.audio.listen')}
            </Typography>
            {title && (
              <Typography contrast="low" colors="primary" className="line-clamp-2 max-w-full flex-wrap text-mdlc">
                {title}
              </Typography>
            )}
          </View>
          <View className="flex h-full items-center justify-center">
            <Icon
              size="xl"
              className="text-primary-text-contrast-default"
              name={isPlaying ? ICON.PAUSE_CIRCLE : ICON.PLAY_CIRCLE}
              testID={isPlaying ? ICON.PAUSE_CIRCLE : ICON.PLAY_CIRCLE}
            />
          </View>
        </Pressable>

        {!shouldHideCard && !!showContent && (
          <View className="mt-2 py-4">
            <AudioPlayer source={source} shouldPlay={isPlaying} onFinished={handleFinished} />
          </View>
        )}
      </CardBody>
    </Card>
  )
}
